import { useEffect, useRef, useState, type FormEvent } from "react";
import type { Session } from "@supabase/supabase-js";
import { Cat, Copy, Plus, X } from "lucide-react";

export type SetupCat = { id: string; name: string };

type FeederClaim = {
  feederId: string;
  deviceId: string;
  name: string;
  expiresAt: string;
  setupCode: string;
};

const PROVISIONING_API_URL = (import.meta.env.VITE_SNAPSHOT_API_URL || "https://catfeeder-ingest.angrypancakes.workers.dev").replace(/\/$/, "");

async function createClaim(session: Session, payload: Record<string, string>): Promise<FeederClaim> {
  const response = await fetch(`${PROVISIONING_API_URL}/v1/feeder-claims`, {
    method: "POST",
    headers: { authorization: `Bearer ${session.access_token}`, "content-type": "application/json" },
    body: JSON.stringify(payload)
  });
  const result = await response.json() as FeederClaim | { error?: string };
  if (!response.ok || !("setupCode" in result)) {
    throw new Error("error" in result && result.error ? result.error : "Could not create the setup code.");
  }
  return result;
}

function SetupCode({ claim, onDone }: { claim: FeederClaim; onDone: () => void }) {
  const [copied, setCopied] = useState(false);
  const copyCode = async () => {
    await navigator.clipboard.writeText(claim.setupCode);
    setCopied(true);
  };
  return <div className="setupCodeResult" role="status">
    <span>Setup code for {claim.name}</span>
    <strong>{claim.setupCode}</strong>
    <small>Expires {new Date(claim.expiresAt).toLocaleString()}</small>
    <ol>
      <li>Make sure the feeder is connected to your home Wi-Fi.</li>
      <li>Open <code>http://catfeeder.local/cloud-setup</code> on the same network.</li>
      <li>Enter this code and choose <strong>Set Up Feeder</strong>.</li>
    </ol>
    <p>If the hostname does not open, use the feeder's local IP address followed by <code>/cloud-setup</code>.</p>
    <div className="setupCodeActions">
      <button type="button" className="secondaryButton" onClick={() => void copyCode()}><Copy size={16} />{copied ? "Copied" : "Copy code"}</button>
      <button type="button" className="primary" onClick={onDone}>Done</button>
    </div>
  </div>;
}

export function FirstFeederSetup({ session, onComplete }: { session: Session; onComplete: () => void }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [claim, setClaim] = useState<FeederClaim | null>(null);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    const data = new FormData(event.currentTarget);
    try {
      setClaim(await createClaim(session, {
        householdName: String(data.get("household") || "").trim(),
        catName: String(data.get("cat") || "").trim(),
        name: String(data.get("feeder") || "").trim(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      }));
    } catch (setupError) {
      setError(setupError instanceof Error ? setupError.message : "Could not finish setup.");
    } finally {
      setSaving(false);
    }
  };

  return <main className="centered"><section className="setupPanel firstFeederSetup">
    <Cat size={30} />
    <h1>Set up your first feeder</h1>
    {claim ? <SetupCode claim={claim} onDone={onComplete} /> : <form onSubmit={submit}>
      <p>Name your household, cat, and feeder. You will receive one short setup code—no device key or terminal needed.</p>
      <label>Household<input name="household" required maxLength={100} placeholder="Home" /></label>
      <label>Cat name<input name="cat" required maxLength={100} /></label>
      <label>Feeder name<input name="feeder" required maxLength={100} placeholder="Kitchen feeder" /></label>
      {error && <p className="error" role="alert">{error}</p>}
      <button className="primary" disabled={saving}>{saving ? "Creating..." : "Create setup code"}</button>
    </form>}
  </section></main>;
}

export function AddFeederDialog({ session, householdId, cats, onClose, onCreated }: {
  session: Session;
  householdId: string;
  cats: SetupCat[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [claim, setClaim] = useState<FeederClaim | null>(null);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    const data = new FormData(event.currentTarget);
    try {
      setClaim(await createClaim(session, {
        householdId,
        catId: String(data.get("catId") || ""),
        name: String(data.get("name") || "").trim()
      }));
      onCreated();
    } catch (setupError) {
      setError(setupError instanceof Error ? setupError.message : "Could not add the feeder.");
    } finally {
      setSaving(false);
    }
  };
  const close = () => { if (!saving) onClose(); };

  return <div className="dialogBackdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) close(); }}>
    <section className="feederSetupDialog" role="dialog" aria-modal="true" aria-labelledby="add-feeder-title">
      <header><div><Plus size={19} /><h2 id="add-feeder-title">Add feeder</h2></div><button type="button" className="iconButton" title="Close" disabled={saving} onClick={close}><X size={18} /></button></header>
      {claim ? <SetupCode claim={claim} onDone={close} /> : <form onSubmit={submit}>
        <p>Choose the cat this feeder should open for. A short setup code will connect the physical feeder to this household.</p>
        <label>Cat<select name="catId" required defaultValue={cats[0]?.id || ""}>{cats.map((cat) => <option key={cat.id} value={cat.id}>{cat.name}</option>)}</select></label>
        <label>Feeder name<input name="name" required maxLength={100} placeholder="Kitchen feeder" autoFocus /></label>
        {!cats.length && <p className="error" role="alert">Add an active cat before adding a feeder.</p>}
        {error && <p className="error" role="alert">{error}</p>}
        <div className="dialogActions"><button type="button" className="secondaryButton" disabled={saving} onClick={close}>Cancel</button><button className="primary" disabled={saving || !cats.length}>{saving ? "Creating..." : "Create setup code"}</button></div>
      </form>}
    </section>
  </div>;
}

export function FeederCodeDialog({ session, feederId, feederName, onClose }: {
  session: Session;
  feederId: string;
  feederName: string;
  onClose: () => void;
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [claim, setClaim] = useState<FeederClaim | null>(null);
  const requestedFor = useRef("");
  const requestCode = async () => {
    setLoading(true);
    setError("");
    try {
      setClaim(await createClaim(session, { feederId }));
    } catch (setupError) {
      setError(setupError instanceof Error ? setupError.message : "Could not create the setup code.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (requestedFor.current === feederId) return;
    requestedFor.current = feederId;
    void requestCode();
  }, [feederId, session.access_token]);

  return <div className="dialogBackdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !loading) onClose(); }}>
    <section className="feederSetupDialog" role="dialog" aria-modal="true" aria-labelledby="setup-feeder-title">
      <header><div><Cat size={19} /><h2 id="setup-feeder-title">Set up {feederName}</h2></div><button type="button" className="iconButton" title="Close" disabled={loading} onClick={onClose}><X size={18} /></button></header>
      {claim ? <SetupCode claim={claim} onDone={onClose} /> : <div className="setupCodeLoading">
        {loading ? <p>Creating a secure one-time code...</p> : <><p className="error" role="alert">{error}</p><div className="dialogActions"><button type="button" className="secondaryButton" onClick={onClose}>Cancel</button><button type="button" className="primary" onClick={() => void requestCode()}>Try again</button></div></>}
      </div>}
    </section>
  </div>;
}
